/**
 * Besieged cities (Phase 13c-2, ADR-0072): a city ringed by enemy soldiers neither heals
 * its defenders nor grows. "Ringed" is `combat.besiegeTiles` or more of the tiles around
 * it held by land soldiers of a civilisation at war with its owner.
 */

import type { Content } from '../../../content/schema/index.js';
import { canAttack } from '../combat/index.js';
import { atWar } from '../diplomacy/index.js';
import { neighborsByDirection } from '../state/grid.js';
import type { City, GameState } from '../state/types.js';
import { allUnits, unitDef, unitIndex } from '../units/index.js';

const ring = new Int32Array(6);

/** How many of the tiles around a city hold enemy land soldiers. */
export function siegeTiles(state: GameState, content: Content, city: City): number {
  const at = state.map.width * city.row + city.col;
  neighborsByDirection(state.map, at, ring);
  const around = new Set<number>();
  for (let d = 0; d < 6; d++) if (ring[d]! !== -1) around.add(ring[d]!);
  const held = new Set<number>();
  for (const unit of allUnits(state)) {
    const there = unitIndex(state.map, unit);
    if (!around.has(there) || held.has(there) || !atWar(state, unit.owner, city.owner)) continue;
    const def = unitDef(content, unit);
    if (def.domain === 'land' && canAttack(def)) held.add(there);
  }
  return held.size;
}

/** Whether a city is under siege: no healing for its defenders, no growth. */
export function isBesieged(state: GameState, content: Content, city: City): boolean {
  return siegeTiles(state, content, city) >= content.rules.combat.besiegeTiles;
}
